import type { LoaderFunctionArgs } from '~/types/route-args';
import React from 'react';
import PaginatedArticles from '@modules/articles/component/paginated-articles/PaginatedArticles';

import { useLoaderData } from 'react-router';
import FeaturedCarousel from '@modules/articles/component/featured-carousel/FeaturedCarousel';
import { fillExtraInfoForArticleCategory } from '@modules/articles/util/fill-company-admin.server';
import { adRequest } from '@modules/adverts/api';
import { response404 } from '~/modules/root/graphql/assertResponse.server';
import { getPaginationOpts } from '~/modules/root/util/get-pagination-opts.server';
import DirectoryHeader from '~/modules/articles/component/directory-header/DirectoryHeader';
import type { loader as archizeenRootLoader } from '~/routes/_app.articles';
import PageSections from '~/modules/professional/component/page-layout/PageSections';
import { shuffleArray } from '~/utils/arrayHelper';
import { mapAdDataToFeatureSlide } from '@modules/articles/util/map-ad-data-to-feature-slide';
import { useLocationMemo } from '@modules/adverts/hook/use-location-memo';
import { getPreloadImageLink } from '~/modules/images';
import {
    createMetaDescription,
    createOGTags,
    getCanonicalHreflang,
    mergeMeta,
} from '~/modules/seo';
import { createMetaTitle } from '~/modules/seo';
import type { APHandle } from '~/modules/root';
import { getArticleCategoryItems } from '~/modules/articles/api/get-article-category-items.server';
import { getPageLocalisation } from '~/modules/seo/api/get-page-localisation.server';
import { getArticleNavigationItems } from '~/modules/articles/api/get-archizeen-common.server';
import { TOP_ARTICLES_COUNT } from '~/modules/articles/api/get-article-directory.server';
import { useCurrentCategory } from '@rocco/ui/website-header/hooks/use-current-category';

export const meta = mergeMeta<typeof loader>(
    () => {
        return [];
    },
    (props) => {
        const { data } = props;

        const title = createMetaTitle(
            props,
            [
                data?.localisation?.Title,
                data?.localisation?.DirectoryHeader?.Title,
            ],
            true,
            data?.pagination?.page,
        );

        const description = createMetaDescription([
            data?.localisation?.Description,
            `Learn about ${data?.localisation?.Title} through our blog of over 200 articles from expert writers and industry professionals.`,
        ]);

        const bannerImage =
            data?.articleCategory?.topReadArticles &&
            data?.articleCategory?.topReadArticles.length > 0
                ? data.articleCategory.topReadArticles[0]?.image.src
                : undefined;

        const preloadImageLinks = bannerImage
            ? data?.deviceType === 'mobile'
                ? getPreloadImageLink(undefined, bannerImage)
                : getPreloadImageLink(bannerImage)
            : [];

        return [
            { title },
            { name: 'description', content: description },
            ...createOGTags({
                meta: props,
                title,
                description,
            }),
            ...getCanonicalHreflang(props),
            ...preloadImageLinks,
        ];
    },
);

export const handle: APHandle = {
    isCategoryPage: true,
    hideContactArchiPro: false,
};

export const loader = async (args: LoaderFunctionArgs) => {
    const { context, request } = args;
    const { device, user } = context;
    const requestUrl = request.url;
    const defaultPageSize = 36;

    const [articleCategory, localisation, bannerAds, navItems] =
        await Promise.all([
            getArticleCategoryItems({
                request: request,
                ...context,
            }),
            getPageLocalisation(args),
            adRequest({
                type: 'ArchizeenBannerPlacement',
                requestUrl,
                ...args.context,
            }),
            getArticleNavigationItems(context),
        ]);

    if (!articleCategory || articleCategory.articles.length === 0) {
        throw response404();
    }

    fillExtraInfoForArticleCategory(user, articleCategory);

    const pagination = getPaginationOpts({
        request,
        device,
        defaultPageSize,
        totalCount: articleCategory?.totalCount || 0,
    });

    const bannerAdsShuffled = bannerAds ? shuffleArray(bannerAds) : [];
    const featuredSlides = bannerAdsShuffled?.length
        ? bannerAdsShuffled
              .slice(0, TOP_ARTICLES_COUNT)
              .map(mapAdDataToFeatureSlide)
        : (articleCategory?.topReadArticles ?? []);

    return {
        deviceType: context.device.type,
        articleCategory,
        localisation,
        bannerAds: bannerAdsShuffled,
        pagination,
        navItems,
        featuredSlides,
    };
};

const ArchizeenCategoryPage = () => {
    const {
        articleCategory,
        pagination,
        localisation,
        navItems,
        featuredSlides,
    } = useLoaderData<typeof loader>();
    const { topReadArticles: _, ...rest } = articleCategory;
    const { common } = useLoaderData<typeof archizeenRootLoader>();

    // Keep the first value returned by loader to avoid changing when loader revalidate
    const featuredSlidesMemoized = useLocationMemo(featuredSlides);

    const { setCategory } = useCurrentCategory();

    // Set the current category for header navigation
    React.useEffect(() => {
        setCategory('Archizeen');
        return () => setCategory(null);
    }, [setCategory]);

    return (
        <>
            <DirectoryHeader
                navItems={navItems ?? []}
                content={
                    localisation?.DirectoryHeader?.ContentTop ||
                    common?.contentTop
                }
                title={localisation?.DirectoryHeader?.Title}
            />
            <PageSections>
                <FeaturedCarousel
                    featuredSlides={featuredSlidesMemoized}
                    imageLazyLoading={'eager'}
                />
                <PaginatedArticles {...rest} {...pagination} />
            </PageSections>
        </>
    );
};

export default ArchizeenCategoryPage;
